import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';

/**
 * Both SalesOrderErpResponse.ErpMessageId and AdxInvoiceErpResponse.ErpMessageId are
 * UNIQUEIDENTIFIER NOT NULL columns — a malformed value fails ASP.NET model binding for the
 * Guid-typed command property with an opaque 400, so this is validated client-side up front.
 */
const GUID_PATTERN = /^\{?([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\}?$/i;

export function guidValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value ?? '').trim();
    if (!value) return null; // let Validators.required own the empty case
    return GUID_PATTERN.test(value) ? null : {invalidGuid: true};
  };
}

/**
 * Every ErpMessageId FMS itself generates (Guid.NewGuid().ToString() in fms-erp-api) renders as
 * lowercase, unbraced, hyphenated — e.g. "3fa85f64-5717-4562-b3fc-2c963f66afa6" — and that's the
 * exact string later used as the Azure Service Bus message's MessageId, so it's what a user
 * would copy from the Service Bus Explorer (Azure Portal) when correlating to a specific
 * in-flight message. Accepts braces/upper-case on input (as some tools display it) but always
 * normalizes to that canonical lowercase/unbraced form before it's saved, so stored values stay
 * consistent no matter where the id was copied from.
 */
export function normalizeGuid(value: string | null | undefined): string {
  const match = GUID_PATTERN.exec((value ?? '').trim());
  return match ? match[1].toLowerCase() : (value ?? '').trim();
}

/**
 * "Sent to ERP" records when the order/invoice was actually processed by ERP — it can never be
 * in the future, since a manual entry can only ever be backfilled after the fact.
 */
export function notFutureDateTimeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null; // let Validators.required own the empty case
    const value = new Date(control.value);
    if (Number.isNaN(value.getTime())) return {invalidDate: true};
    return value.getTime() > Date.now() ? {futureDate: true} : null;
  };
}

/**
 * Requires a non-empty, non-whitespace-only value — Validators.required alone accepts a
 * string of only spaces.
 */
export function requiredTrimmedValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined) return null; // let Validators.required own this
    return (typeof value === 'string' && value.trim().length === 0) ? {required: true} : null;
  };
}
